import {
  HTMLAttributes,
  forwardRef,
  ReactNode,
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
} from 'react';

import { cn } from '../utils/cn';

// Dropdown Context
interface DropdownContextValue {
  open: boolean;
  setOpen: (value: boolean) => void;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

function useDropdownContext() {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('Dropdown components must be used within a Dropdown provider');
  }
  return context;
}

// Dropdown Root
export type DropdownProps = HTMLAttributes<HTMLDivElement>;

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  ({ className, children, ...props }, _ref) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close on escape
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    return (
      <DropdownContext.Provider value={{ open, setOpen }}>
        <div ref={dropdownRef} className={cn('relative inline-block', className)} {...props}>
          {children}
        </div>
      </DropdownContext.Provider>
    );
  }
);

Dropdown.displayName = 'Dropdown';

// Dropdown Trigger
export interface DropdownTriggerProps extends HTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

export const DropdownTrigger = forwardRef<HTMLButtonElement, DropdownTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const { open, setOpen } = useDropdownContext();

    return (
      <button
        ref={ref}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        className={className}
        {...props}
      >
        {children}
      </button>
    );
  }
);

DropdownTrigger.displayName = 'DropdownTrigger';

// Dropdown Content
export interface DropdownContentProps extends HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom';
  sideOffset?: number;
}

export const DropdownContent = forwardRef<HTMLDivElement, DropdownContentProps>(
  (
    {
      className,
      align = 'start',
      side = 'bottom',
      sideOffset: _sideOffset = 4,
      children,
      ...props
    },
    ref
  ) => {
    const { open } = useDropdownContext();

    if (!open) return null;

    return (
      <div
        ref={ref}
        role="menu"
        className={cn(
          'absolute z-50 min-w-[12rem] py-1 bg-[color:var(--ids-surface-raised,#1A2336)] rounded-[var(--ids-radius-md,10px)] shadow-[var(--ids-elev-3,0_12px_32px_-8px_rgba(0,0,0,0.55))] border border-[color:var(--ids-border-strong,rgba(255,255,255,0.14))]',
          'animate-in fade-in-0 zoom-in-95 duration-100',
          {
            'top-full mt-1': side === 'bottom',
            'bottom-full mb-1': side === 'top',
            'left-0': align === 'start',
            'left-1/2 -translate-x-1/2': align === 'center',
            'right-0': align === 'end',
          },
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

DropdownContent.displayName = 'DropdownContent';

// Dropdown Item
export interface DropdownItemProps extends HTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  disabled?: boolean;
  destructive?: boolean;
}

export const DropdownItem = forwardRef<HTMLButtonElement, DropdownItemProps>(
  ({ className, icon, disabled, destructive, children, onClick, ...props }, ref) => {
    const { setOpen } = useDropdownContext();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled) {
        onClick?.(e);
        setOpen(false);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="menuitem"
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
          'focus:outline-none focus:bg-[color:rgba(var(--ids-brand-rgb,59,130,246),0.14)]',
          {
            'text-[color:var(--ids-text-muted,#94A3B8)] hover:bg-[color:rgba(var(--ids-brand-rgb,59,130,246),0.14)] hover:text-[color:var(--ids-text,#F8FAFC)]':
              !destructive && !disabled,
            'text-[color:var(--ids-danger,#EF4444)] hover:bg-[color:rgba(var(--ids-danger-rgb,239,68,68),0.10)]':
              destructive && !disabled,
            'opacity-50 cursor-not-allowed': disabled,
          },
          className
        )}
        {...props}
      >
        {icon && <span className="w-4 h-4 flex-shrink-0">{icon}</span>}
        {children}
      </button>
    );
  }
);

DropdownItem.displayName = 'DropdownItem';

// Dropdown Separator
export type DropdownSeparatorProps = HTMLAttributes<HTMLDivElement>;

export const DropdownSeparator = forwardRef<HTMLDivElement, DropdownSeparatorProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="separator"
      className={cn('h-px my-1 bg-[color:var(--ids-border,rgba(255,255,255,0.08))]', className)}
      {...props}
    />
  )
);

DropdownSeparator.displayName = 'DropdownSeparator';

// Dropdown Label
export type DropdownLabelProps = HTMLAttributes<HTMLDivElement>;

export const DropdownLabel = forwardRef<HTMLDivElement, DropdownLabelProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-3 py-2 text-xs font-semibold text-[color:var(--ids-text-subtle,#7E8DA6)] uppercase tracking-[0.08em]',
        className
      )}
      {...props}
    />
  )
);

DropdownLabel.displayName = 'DropdownLabel';
